import { useQueryClient, useMutation } from '@tanstack/react-query'
import { useIonRouter } from '@ionic/react'
import * as R from 'remeda'

import { getFullName } from '../../utils/format-helpers'
import { client } from '../../services/client'
import { useBroadcastActions } from './broadcast-store'
import { useAccount, useUser } from '../../contexts/authContext'
import { useToast } from '../../contexts/toastContext'
import { accountQueryKeys } from '../../hooks/account/queries'
import { chatQueryKeys } from '../../hooks/chat/queries'
import { clientQueryKeys } from '../../hooks/clients/queries'
import { outreachQueryKeys } from '../../hooks/outreach/queries'
import { useIsAdminRoute } from '../../hooks/routing'
import { createBroadcastResponseSchema } from './broadcast.schema'
import { broadcastQueryKeys } from './queries'

import type {
  BroadcastDetail,
  CreateBroadcastInputs,
  FromUserRule,
} from './broadcast.schema'
import type { ServerFilters } from '../../clients/client-filters.schema'

export type QueueBroadcastInput = {
  title: string
  date?: string | null
  smsClientIds?: Array<string> // associateOnly
  emailClientIds?: Array<string> // associateOnly
  smsMessage?: string
  emailSubject?: string
  emailMessage?: string
  smsTemplateId?: string
  emailTemplateId?: string
  collectionIdsSms?: Array<string>
  collectionIdsEmail?: Array<string>
  lookIdsSms?: Array<string>
  lookIdsEmail?: Array<string>
  productIdsSms?: Array<string>
  productIdsEmail?: Array<string>
  setAttachmentsSms?:
    | Array<{ content: string; name: string }>
    | Array<string>
    | null
  setAttachmentsEmail?:
    | Array<{ content: string; name: string }>
    | Array<string>
    | null
  type:
    | 'associate'
    | 'adminSharedOnly'
    | 'adminAssignedOnly'
    | 'adminAll'
    | 'admin'
  callListId?: string
  callListDynamicId?: string
  filter?: ServerFilters
  unlayerDesign?: any // TODO: TEMP UNTIL SCHEMA IS USED AS SOURCE OF TRUTH
  fromUserRule: FromUserRule
  fromUserId: string
  fromName: string | null
  fromEmail: string | null
}

async function createAdminBroadcast(params: Partial<CreateBroadcastInputs>) {
  const {
    messageType = 'prioSmsEmail',
    fromUserId: fromUserIdParam,
    filter: filterParam,
    ...rest
  } = params

  const fromUserId = fromUserIdParam ?? null

  const filter = {
    admin: true,
    subscribersOnly: true,
    notContacted: { day: 1 },
    ...filterParam,
  }

  const response = await client
    .post('functions/createBroadcast', {
      json: {
        filter,
        fromUserId,
        messageType,
        type: 'admin',
        ...rest,
      },
    })
    .json()

  return createBroadcastResponseSchema.parse(response).result.broadcast
}

async function deleteBroadcast(id: string) {
  return client
    .post('functions/deleteBroadcast', {
      json: {
        objectId: id,
      },
    })
    .json()
}

async function createBroadcast(params: Partial<CreateBroadcastInputs>) {
  const response = await client
    .post('functions/createBroadcast', {
      json: { ...params },
    })
    .json()

  return createBroadcastResponseSchema.parse(response).result.broadcast
}

async function queueBroadcast(props: QueueBroadcastInput) {
  return client.post('functions/queueBroadcast', { json: props }).json()
}

async function updateBroadcast({
  objectId,
  broadcast,
  isPartial = false,
}: {
  objectId: string
  broadcast: Partial<CreateBroadcastInputs>
  isPartial?: boolean
}) {
  const updatedBroadcast = isPartial
    ? broadcast
    : {
        ...broadcast,
        filter: broadcast.filter ? broadcast.filter : undefined,
        emailMessage: broadcast.emailMessage || null,
        unlayerDesign: broadcast.unlayerDesign ?? null,
        smsTemplateId: broadcast.smsTemplateId ?? null,
        emailTemplateId: broadcast.emailTemplateId ?? null,
        collectionIdsSms: broadcast.collectionIdsSms ?? null,
        lookIdsSms: broadcast.lookIdsSms ?? null,
        productIdsSms: broadcast.productIdsSms ?? null,
        collectionIdsEmail: broadcast.collectionIdsEmail ?? null,
        lookIdsEmail: broadcast.lookIdsEmail ?? null,
        productIdsEmail: broadcast.productIdsEmail ?? null,
        setAttachmentsSms: broadcast.setAttachmentsSms,
        setAttachmentsEmail: broadcast.setAttachmentsEmail,
      }

  const response = await client
    .post('functions/updateBroadcast', {
      json: { objectId, ...updatedBroadcast },
    })
    .json()

  return createBroadcastResponseSchema.parse(response).result.broadcast
}

function useCreateAdminBroadcast() {
  const queryClient = useQueryClient()

  const setToast = useToast()
  const user = useUser()
  const account = useAccount()

  let fromName: string | null = null
  let fromEmail: string | null = null

  if (user.hasSendGrid && account.sendgridDomain) {
    fromName = user.displayName ?? getFullName(user)
    fromEmail = user.twilioProxyEmail
  }

  return useMutation(
    (broadcastData: Partial<CreateBroadcastInputs>) =>
      createAdminBroadcast({
        fromUserRule: 'fromUser',
        fromUserId: user.objectId,
        fromName,
        fromEmail,
        ...broadcastData,
      }),
    {
      onSettled: (data) => {
        queryClient.invalidateQueries(broadcastQueryKeys.list('admin'))
        if (data?.objectId)
          queryClient.invalidateQueries(
            broadcastQueryKeys.detail(data.objectId)
          )
      },
      onError: (error) => {
        if (
          error instanceof Error &&
          error.message === 'invalid email domain'
        ) {
          setToast({
            message:
              'You have not finished setting up your email. Please contact your OneShop account manager.',
          })
        }
      },
    }
  )
}

function useCreateBroadcast() {
  const queryClient = useQueryClient()
  const setToast = useToast()

  return useMutation(createBroadcast, {
    onSettled: (data) => {
      queryClient.invalidateQueries(broadcastQueryKeys.list())
      if (data?.objectId)
        queryClient.invalidateQueries(broadcastQueryKeys.detail(data.objectId))
    },
    onSuccess: () =>
      setToast({
        message: 'Your broadcast draft was successfully saved.',
        color: 'yellow',
      }),
  })
}

function useDeleteBroadcast() {
  const queryClient = useQueryClient()

  const router = useIonRouter()
  const setToast = useToast()

  return useMutation(deleteBroadcast, {
    onSettled: () => {
      queryClient.invalidateQueries(broadcastQueryKeys.lists())
    },
    onSuccess: () => {
      router.push('/tabs/admin/broadcasts', 'back', 'pop')
      setToast({
        message: 'Your broadcast draft was successfully deleted.',
        color: 'yellow',
      })
    },
    onError: (error, variables, context) => {
      const errorMessage =
        error instanceof Error ? error.message : 'unknown error.'

      setToast({
        message: `Something went wrong trying to delete the draft: ${errorMessage}`,
      })
    },
  })
}

function useQueueBroadcast() {
  const setToast = useToast()
  const router = useIonRouter()

  const queryClient = useQueryClient()
  const { reset: resetBroadcast } = useBroadcastActions()
  const isAdminRoute = useIsAdminRoute()

  return useMutation(queueBroadcast, {
    onSettled: (_data, _error, variables) => {
      // NOTE: Due to possible timeouts on server ASSUME all went well until more robust handling implemented
      // timeouts might not mean broadcasts are not saved and
      // we don't want users to re-send broadcasts that actually did get saved and started
      resetBroadcast()

      queryClient.invalidateQueries(broadcastQueryKeys.lists())

      // TODO: possibly add some setQueryData to guarantee lastContact status in case of timeouts (see above)
      queryClient.invalidateQueries(chatQueryKeys.inbox(), {
        refetchType: 'inactive',
      })
      queryClient.invalidateQueries(chatQueryKeys.conversations())

      // invalidate relevant client details for up-to-date msg related data... lastcontact, etc
      // const clientIds = Array.from(
      //   new Set([...variables.emailClientIds, ...variables.smsClientIds])
      // )
      // const clientKeys = clientIds.map((id) => clientQueryKeys.detail(id))

      // invalidate all client lists/search results
      // TODO: search cache key still on v1 query keys
      queryClient.invalidateQueries(clientQueryKeys.lists())
      queryClient.invalidateQueries(['search'])

      // queryClient.invalidateQueries({
      //   predicate: (q) => clientKeys.some((key) => R.equals(key, q.queryKey)),
      // })
      queryClient.invalidateQueries(clientQueryKeys.details())

      // outreach/call lists
      if (variables.callListId) {
        queryClient.invalidateQueries(
          outreachQueryKeys.detail({ id: variables.callListId })
        )
        queryClient.invalidateQueries(
          outreachQueryKeys.list({ type: 'One Time' })
        )
      }

      if (variables.callListDynamicId) {
        queryClient.invalidateQueries(
          outreachQueryKeys.detail({ id: variables.callListDynamicId })
        )
        queryClient.invalidateQueries(
          outreachQueryKeys.list({ type: 'Dynamic' })
        )
      }

      // invalidate sms credits
      queryClient.invalidateQueries(accountQueryKeys.credits())

      // NAVIGATE BACK
      const url = isAdminRoute ? '/tabs/admin/broadcasts' : '/broadcasts'
      // navigation back - USE POP, seems that replace will only replace url
      router.push(url, 'back', 'pop')
    },
    onSuccess: () => {
      setToast({
        message: 'Congrats! Your message is sending!',
        color: 'success',
        duration: '10000',
      })
    },
    onError: (error) => {
      // only display error toast if it was not a timeout error (SEE onSettled FOR EXPLANATION)
      if (error instanceof Error && error.name !== 'TimeoutError') {
        const errorMessage = error.message ?? 'Unknown Error.'

        setToast({
          message: `Something went wrong while sending your broadcast. ${errorMessage}`,
          color: 'danger',
          duration: 10000,
        })
      }
    },
  })
}

function useSendBroadcast() {
  const queryClient = useQueryClient()
  const setToast = useToast()

  return useMutation(
    async (broadcastId: string) =>
      client
        .post('functions/sendBroadcast', { json: { objectId: broadcastId } })
        .json<BroadcastDetail>(),
    {
      onSettled: (data, _error, variables) => {
        queryClient.invalidateQueries(broadcastQueryKeys.lists())
        queryClient.invalidateQueries(broadcastQueryKeys.detail(variables))

        queryClient.invalidateQueries({
          predicate: (q) =>
            R.equals(
              q.queryKey,
              broadcastQueryKeys.meta({ id: variables, meta: 'counts' })
            ) && data?.status === 'draft'
              ? true
              : false,
        })
      },
      onSuccess: () => {
        setToast({
          message: 'Your broadcast has been queued up to be sent!',
          color: 'yellow',
          duration: 5000,
        })
      },
      onError: (error) => {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown Error'

        setToast({
          message: `Something went wrong while saving your draft: ${errorMessage}.`,
          color: 'danger',
        })
      },
    }
  )
}

function useUpdateBroadcast() {
  const queryClient = useQueryClient()
  const setToast = useToast()

  return useMutation(updateBroadcast, {
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(broadcastQueryKeys.lists())
      queryClient.invalidateQueries(
        broadcastQueryKeys.detail(variables.objectId)
      )
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown Error'

      setToast({
        message: `Something went wrong while saving your draft: ${errorMessage}.`,
        color: 'danger',
      })
    },
  })
}

function useUpdateBroadcastFilter() {
  const queryClient = useQueryClient()
  const setToast = useToast()
  const updateBroadcast = useUpdateBroadcast()

  return useMutation(
    async ({ id, filter }: { id: string; filter: ServerFilters }) => {
      try {
        await updateBroadcast.mutateAsync({
          objectId: id,
          broadcast: { filter },
        })
      } catch (e) {
        return Promise.reject(
          new Error('Something went wrong updating the filters.')
        )
      }
    },
    {
      onSettled: (_data, _error, variables) => {
        queryClient.invalidateQueries(broadcastQueryKeys.lists())
        queryClient.invalidateQueries(broadcastQueryKeys.detail(variables.id))
      },

      onError: (error) => {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown Error'

        setToast({
          message: `Something went wrong while saving your draft: ${errorMessage}.`,
          color: 'danger',
        })
      },
    }
  )
}

export {
  useCreateBroadcast,
  useCreateAdminBroadcast,
  useDeleteBroadcast,
  useQueueBroadcast,
  useSendBroadcast,
  useUpdateBroadcast,
  useUpdateBroadcastFilter,
}
